/*!

=========================================================
* Vision UI Free Chakra - v1.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/vision-ui-free-chakra
* Copyright 2021 Creative Tim (https://www.creative-tim.com/)
* Licensed under MIT (https://github.com/creativetimofficial/vision-ui-free-chakra/blob/master LICENSE.md)

* Design and Coded by Simmmple & Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import React, { useEffect, useState } from "react";
import {
  Avatar,
  Badge,
  Button,
  Flex,
  Select,
  Td,
  Text,
  Tr,
  Icon,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  FormControl,
  FormLabel,
  Checkbox,
  Input,
  Box,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import GradientBorder from "components/GradientBorder/GradientBorder";
import { CopyIcon } from "@chakra-ui/icons";

function TablesTableRow(props) {
  const { data, lastItem, isOpen, setOpen, openIndex, currentIndex } = props;

  const [showFullScript, setShowFullScript] = useState(false);
  const [copyLabel, setCopyLabel] = useState("Copy to clipboard");

  const script = `<script>startTime=0; function calculateUserTimeSpent(){const endTime=performance.now(); const timeSpentInMinutes=(endTime - startTime) / 1000 / 60; let timeSpentInSeconds=1; timeSpentInSeconds=Math.max(0, Math.floor(timeSpentInMinutes * 60)); return timeSpentInSeconds;}function startTrackingTime(){startTime=performance.now();}async function getUserIP(){try{const response=await fetch("https://api.ipify.org?format=json"); const data=await response.json(); return data.ip;}catch (error){console.error("Error getting user IP:", error); return "127.0.0.1";}}function sendPixelTrackerOnUnload(){const userTimeSpent=calculateUserTimeSpent(); getUserIP().then((userIp)=>{const campaign_uid='f2d93239-ae17-4680-a7e3-8f369fe7e405'; const publisher_uuid = 'd9a87975-2534-4379-b1b9-a28c52e540e4'; const client_uuid='532bbd57-1d3e-4360-a94e-031cd87184eb'; const apiUrl=\`https://pr.reachableads.com/api/pixel_tracker/?campaign_uid=\${campaign_uid}&publisher_uuid=\${publisher_uuid}&client_uuid=\${client_uuid}&user_ip=\${userIp}&user_time_spent=\${userTimeSpent}&url=\${window.location.href}\`; const image=new Image(); image.src=apiUrl; console.log("Pixel Tracker API request sent.");});}window.addEventListener("load", startTrackingTime); setTimeout(function (){sendPixelTrackerOnUnload();}, 1000); window.addEventListener("beforeunload", sendPixelTrackerOnUnload); document.addEventListener("visibilitychange", handleVisibilityChange); function handleVisibilityChange(){if (document.visibilityState==="hidden"){sendPixelTrackerOnUnload();}}</script>`;

  const titleColor = "white";
  const textColor = useColorModeValue("gray.700", "white");
  const bgStatus = useColorModeValue("gray.400", "#1a202c");
  const colorStatus = useColorModeValue("white", "gray.400");
  const handleSelectChange = (event) => {
    console.log("Selected value:", event.target.value);
  };

  if (data === undefined || data === null || Object.keys(data).length === 0) {
    return (
      <Tr>
        <Td
          ps="0px"
          border={lastItem ? "none" : null}
          borderBottomColor="#56577A"
        >
          <Text
            fontSize="sm"
            color="#fff"
            fontWeight="normal"
            textAlign={"center"}
          >
            No Data Found
          </Text>
        </Td>
      </Tr>
    );
  }

  const rowVariants = {
    hidden: { opacity: 0, x: 100 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { duration: 0.5, ease: "easeInOut", delay: i * 0.25 }, // Stagger effect
    }),
    exit: { opacity: 0, x: -100, transition: { duration: 0.3 } },
  };

  return (
    <AnimatePresence>
      <motion.tr
        key={data.id || currentIndex} // Ensure unique key for re-rendering
        variants={rowVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        custom={currentIndex} // Pass index for stagger effect
        _hover={{
          bg: "#016D6D7E",
        }}
        color={colorStatus}
      >
        {Object.values(data).map((value, index) => {
          return (
            <Td
              ps="0px"
              border={lastItem ? "none" : null}
              borderBottomColor="#56577A"
              key={index + value}
              textAlign={"center"}
            >
              {value}
            </Td>
          );
        })}

        <Modal
          isOpen={openIndex === currentIndex && isOpen}
          onClose={setOpen}
          size="3xl"
        >
          <ModalOverlay />
          <ModalContent
            p="20px"
            bg={{
              base: "rgb(19,21,56)",
            }}
          >
            <ModalHeader>
              {" "}
              <Text fontSize="lg" color="#fff" fontWeight="bold">
                Tag
              </Text>
            </ModalHeader>
            <ModalCloseButton color="#fff" />
            <ModalBody>
              <Flex
                background="transparent"
                color="#fff"
                borderRadius="30px"
                direction="column"
                p="12px"
                w="100%"
                bg="rgb(19,21,56)"
              >
                <Box
                  p={4}
                  borderWidth="1px"
                  borderRadius="lg"
                  borderColor="gray.600"
                >
                  <Flex align="center" justify="space-between">
                    <Box
                      fontSize="sm"
                      maxW="70%"
                      whiteSpace="pre-wrap"
                      overflowWrap="break-word"
                    >
                      {showFullScript ? script : `${script.slice(0, 50)}...`}
                    </Box>
                    <Flex gap={2}>
                      <Tooltip label={copyLabel} closeOnClick={false} hasArrow>
                        <IconButton
                          icon={<CopyIcon />}
                          colorScheme="teal"
                          variant="outline"
                          borderRadius="full"
                          fontSize="20px"
                          aria-label="Copy"
                          size="sm"
                          onClick={() => {
                            navigator.clipboard.writeText(script);
                            setCopyLabel("Copied");
                            setTimeout(
                              () => setCopyLabel("Copy to clipboard"),
                              1500
                            );
                          }}
                        />
                      </Tooltip>
                      <Button
                        size="sm"
                        colorScheme="teal"
                        onClick={() => setShowFullScript(!showFullScript)}
                      >
                        {showFullScript ? "HIDE" : "SHOW"}
                      </Button>
                    </Flex>
                  </Flex>
                </Box>
              </Flex>
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" mr={3} onClick={setOpen}>
                Close
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </motion.tr>
    </AnimatePresence>
  );
}

export default TablesTableRow;
